import {
  collection,
  getDocs,
  getDocsFromCache,
  query,
  orderBy,
  getFirestore,
  onSnapshot,
  Unsubscribe,
  QuerySnapshot,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import type { Conversation } from '../../types/Inbox';
import { getFirebaseApp } from '../firebaseApp';

/**
 * Conversation Cache Service
 * Reads conversation list from Firestore wix-chat database
 * Frontend uses this ONLY - no direct Wix API calls for conversation list
 * 
 * Conversations are ordered by lastMessageTime for proper sorting
 */
export class ConversationCacheService {
  private static instance: ConversationCacheService | null = null;
  private unsubscribers: Map<string, Unsubscribe> = new Map();

  static getInstance(): ConversationCacheService {
    if (!ConversationCacheService.instance) {
      ConversationCacheService.instance = new ConversationCacheService();
    }
    return ConversationCacheService.instance;
  }

  private getFirestore() {
    return getFirestore(getFirebaseApp(), 'wix-chat');
  }

  // Single source of truth for turning a raw Firestore conversation/draft document into a
  // Conversation object. All read paths (one-shot fetch, real-time listener, drafts listener)
  // funnel through this so a field added/changed here can't silently be missing from just one
  // of them — that exact gap (contactId present in 3 of 4 hand-written mapping blocks) once
  // caused a contact's avatar to load correctly on the initial fetch and then revert as soon
  // as the real-time listener's own separate mapping (missing the field) took over.
  private mapDocToConversation(id: string, data: Record<string, any>): Conversation {
    // Unread is per staff member (like Wix Inbox): `unreadBy.<uid>` counts what THAT person has not opened, so one person
    // opening a conversation never clears it for anyone else.
    const uid = getAuth(getFirebaseApp()).currentUser?.uid;
    const myUnread = uid ? Number(data.unreadBy?.[uid]) || 0 : 0;
    return {
      id: data.id || data.conversationId || id,
      name: data.name || data.contactName || 'Unknown',
      lastMessage: data.lastMessage || '',
      timestamp: data.timestamp?.toDate?.() || new Date(data.timestamp || 0),
      messages: [],
      avatarUrl: data.avatarUrl || '',
      contactId: data.contactId || data.participantContactId || undefined,
      unread: myUnread > 0,
      unreadCount: myUnread,
      starred: data.starred || false,
      archived: data.archived || false,
      priority: data.priority || 'medium',
      channelType: data.channelType || 'CHAT',
    } as Conversation;
  }

  /**
   * Get all cached conversations from Firestore
   * Ordered by lastMessageTime (newest first)
   * 
   * IMPORTANT: If conversation has participantContactId but no name,
   * this will look up the contact name from Wix API
   * 
   * @returns Array of conversations sorted by most recent message
   */
  async getCachedConversations(): Promise<Conversation[]> {
    const db = this.getFirestore();

    try {
      console.log(`📭 Loading cached conversations from Firestore`);
      const conversationsRef = collection(db, 'conversations');
      const q = query(conversationsRef, orderBy('timestamp', 'desc'));
      // The device's own copy first: the list shows at once, and the real-time listener started alongside
      // (subscribeToConversations) brings anything newer. Only an empty local copy (first run) asks the server —
      // getDocs always does, which was a 2-3 second wait on every open of the Inbox.
      let snapshot = await getDocsFromCache(q).catch(() => null);
      if (!snapshot || snapshot.empty) snapshot = await getDocs(q);

      const conversations: Conversation[] = [];

      for (const doc of snapshot.docs) {
        const data = doc.data();

        // Skip archived/merged conversations - they're just redirects to the merged conversation
        if (data._archived || data.mergedInto) {
          console.log(`⏭️  Skipping merged/archived conversation ${data.id || data.conversationId}`);
          continue;
        }

        conversations.push(this.mapDocToConversation(doc.id, data));
      }

      console.log(`✅ Retrieved ${conversations.length} cached conversations`);
      return conversations;
    } catch (error) {
      console.error(`❌ Error retrieving cached conversations:`, error);
      return [];
    }
  }

  /**
   * Subscribe to real-time conversation updates
   * Automatically updates UI when conversations change (new messages, etc)
   * Resolves participantContactId to contact names
   * 
   * @param callback - Function to call with updated conversations
   * @returns Unsubscribe function to stop listening
   */
  subscribeToConversations(
    callback: (conversations: Conversation[]) => void,
    onError?: (error: unknown) => void
  ): Unsubscribe {
    const db = this.getFirestore();

    try {
      const conversationsRef = collection(db, 'conversations');
      const q = query(conversationsRef, orderBy('timestamp', 'desc'));

      const unsubscribe = onSnapshot(
        q,
        async (snapshot: QuerySnapshot) => {
          const conversations: Conversation[] = [];

          for (const doc of snapshot.docs) {
            const data = doc.data();

            // Skip archived/merged conversations - they're just redirects to the merged conversation
            if (data._archived || data.mergedInto) {
              console.log(`⏭️  Skipping merged/archived conversation ${data.id || data.conversationId}`);
              continue;
            }

            conversations.push(this.mapDocToConversation(doc.id, data));
          }

          console.log(`📭 Real-time update: ${conversations.length} conversations`);
          callback(conversations);
        },
        (error) => {
          console.error(`❌ Error listening to conversations:`, error);
          onError?.(error);
        }
      );

      // Store unsubscriber for cleanup
      this.unsubscribers.set('conversations', unsubscribe);

      return unsubscribe;
    } catch (error) {
      console.error(`❌ Error subscribing to conversations:`, error);
      return () => {};
    }
  }

  /**
   * Stop listening to real-time conversation updates
   */
  unsubscribeFromConversations(): void {
    const unsubscribe = this.unsubscribers.get('conversations');

    if (unsubscribe) {
      unsubscribe();
      this.unsubscribers.delete('conversations');
      console.log(`✅ Unsubscribed from conversations`);
    }
  }

  /**
   * Get conversation count
   */
  async getConversationCount(): Promise<number> {
    const db = this.getFirestore();

    try {
      const conversationsRef = collection(db, 'conversations');
      const snapshot = await getDocs(conversationsRef);
      return snapshot.size;
    } catch (error) {
      console.error(`❌ Error getting conversation count:`, error);
      return 0;
    }
  }

  /**
   * Search conversations by name or message content
   */
  async searchConversations(searchText: string): Promise<Conversation[]> {
    const conversations = await this.getCachedConversations();

    const lowerSearchText = searchText.toLowerCase();
    return conversations.filter(conv =>
      conv.name.toLowerCase().includes(lowerSearchText) ||
      conv.lastMessage.toLowerCase().includes(lowerSearchText)
    );
  }

  /**
   * Get unread conversations only
   */
  async getUnreadConversations(): Promise<Conversation[]> {
    const conversations = await this.getCachedConversations();
    return conversations.filter(conv => conv.unread || (conv.unreadCount && conv.unreadCount > 0));
  }

  /**
   * Get archived conversations only
   */
  async getArchivedConversations(): Promise<Conversation[]> {
    const conversations = await this.getCachedConversations();
    return conversations.filter(conv => conv.archived);
  }

  /**
   * Subscribe to conversationDrafts for instant UI updates
   * Drafts are written immediately when sending a message (optimistic UI)
   * They contain participant metadata that the webhook will merge into canonical conversations
   *
   * @param callback - Function to call with draft conversations
   * @returns Unsubscribe function to stop listening
   */
  subscribeToConversationDrafts(
    callback: (drafts: Conversation[]) => void
  ): Unsubscribe {
    const db = this.getFirestore();

    try {
      const draftsRef = collection(db, 'conversationDrafts');
      const q = query(draftsRef, orderBy('timestamp', 'desc'));

      const unsubscribe = onSnapshot(
        q,
        (snapshot: QuerySnapshot) => {
          const drafts: Conversation[] = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              ...this.mapDocToConversation(doc.id, data),
              id: data.conversationId || doc.id,
              unread: false,
              unreadCount: 0,
              starred: false,
              archived: false,
              _isDraft: true, // Mark as draft for UI logic
              _draftStatus: data.status || 'sending'
            } as Conversation & { _isDraft?: boolean; _draftStatus?: string };
          });

          console.log(`📝 Real-time draft update: ${drafts.length} drafts`);
          callback(drafts);
        },
        (error) => {
          console.error(`❌ Error listening to conversationDrafts:`, error);
        }
      );

      // Store unsubscriber for cleanup
      this.unsubscribers.set('conversationDrafts', unsubscribe);

      return unsubscribe;
    } catch (error) {
      console.error(`❌ Error subscribing to conversationDrafts:`, error);
      return () => {};
    }
  }

  /**
   * Stop listening to conversationDrafts updates
   */
  unsubscribeFromConversationDrafts(): void {
    const unsubscribe = this.unsubscribers.get('conversationDrafts');

    if (unsubscribe) {
      unsubscribe();
      this.unsubscribers.delete('conversationDrafts');
      console.log(`✅ Unsubscribed from conversationDrafts`);
    }
  }

  /**
   * Clean up all subscriptions
   */
  unsubscribeAll(): void {
    this.unsubscribers.forEach(unsub => {
      try {
        unsub();
      } catch (err) {
        console.error('Error unsubscribing:', err);
      }
    });
    this.unsubscribers.clear();
    console.log('✅ All subscriptions cleaned up');
  }
}
